import { useRouter } from 'expo-router';
import {
    Building2,
    CalendarDays,
    ChevronLeft,
    ChevronRight,
    Flame,
    Globe,
    Layers,
    LayoutDashboard,
    MapPin,
    MessageSquare,
    Plus,
    RefreshCw,
    RotateCcw,
    Search,
    Send,
    Shield,
    SlidersHorizontal,
    Sparkles,
    Star,
    Tag,
    Trash2,
    Upload,
    Users,
    X
} from 'lucide-react-native';
import { useCallback, useEffect, useState } from 'react';
import {
    ActivityIndicator,
    Image,
    Modal,
    Pressable,
    ScrollView,
    StyleSheet,
    Text,
    TextInput,
    View,
    useWindowDimensions
} from 'react-native';

import { Screen } from '~/components/Screen';
import { TopNav } from '~/components/TopNav';
import { SelectRateCard } from '~/components/SelectRateCard';
import { CollectionHotelCard } from '~/components/CollectionHotelCard';
import { toCmsHotel } from '~/lib/homepageCms';
import { api } from '~/api';
import { errorMessage } from '~/api/client';
import { resolveThumbnailSource } from '~/lib/images';
import { useMember } from '~/stores/useMember';
import { formatMoney } from '~/lib/money';
import { colors, fontFamily, maxContentWidth, radii, shadows, spacing } from '~/ui/tokens';

type AdminMenu = 'overview' | 'cms' | 'users' | 'bookings' | 'support';
type CmsSectionKey = Api.Cms.Section['key'];

function AdminScreen() {
    const router = useRouter();
    const isMember = useMember(state => state.isMember);
    const user = useMember(state => state.user);
    const openPrompt = useMember(state => state.openPrompt);
    const { width } = useWindowDimensions();
    const compact = width < 840;

    const [activeMenu, setActiveMenu] = useState<AdminMenu>('overview');
    const [notice, setNotice] = useState<string | null>(null);

    // Overview state
    const [overviewData, setOverviewData] = useState<Api.Admin.Overview.Response['data'] | null>(null);
    const [overviewLoading, setOverviewLoading] = useState(false);

    // CMS state
    const [cmsSections, setCmsSections] = useState<Record<string, Api.Cms.Section> | null>(null);
    const [activeCmsTab, setActiveCmsTab] = useState<CmsSectionKey>('best_deals');
    const [cmsLoading, setCmsLoading] = useState(false);
    const [editingItem, setEditingItem] = useState<{ index: number | null; item: Api.Cms.Item } | null>(null);
    const [hotelSearchQuery, setHotelSearchQuery] = useState('');
    const [hotelSearchResults, setHotelSearchResults] = useState<Api.Admin.SearchHotels.HotelItem[]>([]);
    const [hotelSearching, setHotelSearching] = useState(false);
    const [isHotelSearchFocused, setIsHotelSearchFocused] = useState(false);
    const [searchingHotel, setSearchingHotel] = useState(false);
    const [showOverrides, setShowOverrides] = useState(false);
    const [cmsImportPreview, setCmsImportPreview] = useState<NonNullable<Api.Cms.ImportPreview.Response['data']> | null>(null);
    const [cmsImportSource, setCmsImportSource] = useState<{ csv: string; fileName: string } | null>(null);
    const [cmsImporting, setCmsImporting] = useState(false);
    const [publishingImportKey, setPublishingImportKey] = useState<CmsSectionKey | null>(null);

    // Users state
    const [usersList, setUsersList] = useState<Api.Admin.Users.UserItem[] | null>(null);
    const [usersPage, setUsersPage] = useState(1);
    const [usersTotalPages, setUsersTotalPages] = useState(1);
    const [usersTotal, setUsersTotal] = useState(0);
    const [usersLoading, setUsersLoading] = useState(false);
    const [userSearch, setUserSearch] = useState('');

    // Bookings state
    const [bookingsList, setBookingsList] = useState<Api.Admin.Bookings.BookingItem[] | null>(null);
    const [bookingsPage, setBookingsPage] = useState(1);
    const [bookingsTotalPages, setBookingsTotalPages] = useState(1);
    const [bookingsTotal, setBookingsTotal] = useState(0);
    const [bookingsLoading, setBookingsLoading] = useState(false);
    const [cancellingOrderId, setCancellingOrderId] = useState<string | null>(null);

    // Support state
    const [ticketsList, setTicketsList] = useState<Api.Support.Ticket[] | null>(null);
    const [ticketsPage, setTicketsPage] = useState(1);
    const [ticketsTotalPages, setTicketsTotalPages] = useState(1);
    const [ticketsTotal, setTicketsTotal] = useState(0);
    const [supportLoading, setSupportLoading] = useState(false);
    const [ticketFilter, setTicketFilter] = useState<'all' | Api.Support.Ticket['status']>('all');
    const [selectedTicket, setSelectedTicket] = useState<Api.Support.Ticket | null>(null);
    const [replyText, setReplyText] = useState('');
    const [replying, setReplying] = useState(false);

    const loadOverview = useCallback(async () => {
        setOverviewLoading(true);
        try {
            const res = await api.admin.overview();
            if (res.data) setOverviewData(res.data);
        } catch {
            // error
        } finally {
            setOverviewLoading(false);
        }
    }, []);

    const loadCms = useCallback(async () => {
        setCmsLoading(true);
        try {
            const res = await api.cms.list();
            if (res.data) setCmsSections(res.data);
        } catch {
            // error
        } finally {
            setCmsLoading(false);
        }
    }, []);

    const loadUsers = useCallback(async (page: number = 1, search: string = userSearch) => {
        setUsersLoading(true);
        try {
            const res = await api.admin.users({ page, limit: 15, search });
            if (res.data) {
                setUsersList(res.data.users);
                setUsersPage(res.data.page);
                setUsersTotalPages(res.data.totalPages);
                setUsersTotal(res.data.total);
            }
        } catch {
            // error
        } finally {
            setUsersLoading(false);
        }
    }, [userSearch]);

    const loadBookings = useCallback(async (page: number = 1) => {
        setBookingsLoading(true);
        try {
            const res = await api.admin.bookings({ page, limit: 10 });
            if (res.data) {
                setBookingsList(res.data.bookings);
                setBookingsPage(res.data.page);
                setBookingsTotalPages(res.data.totalPages);
                setBookingsTotal(res.data.total);
            }
        } catch {
            // error
        } finally {
            setBookingsLoading(false);
        }
    }, []);

    const loadTickets = useCallback(async (page: number = 1, status: string = ticketFilter) => {
        setSupportLoading(true);
        try {
            const res = await api.admin.tickets({ page, limit: 10, status: status === 'all' ? undefined : status });
            if (res.data) {
                setTicketsList(res.data.tickets);
                setTicketsPage(res.data.page);
                setTicketsTotalPages(res.data.totalPages);
                setTicketsTotal(res.data.total);
                if (selectedTicket) {
                    const refreshed = res.data.tickets.find(t => t.ticketId === selectedTicket.ticketId);
                    if (refreshed) setSelectedTicket(refreshed);
                }
            }
        } catch {
            // error
        } finally {
            setSupportLoading(false);
        }
    }, [ticketFilter, selectedTicket]);

    useEffect(() => {
        if (!user?.isAdmin) return;
        if (activeMenu === 'overview') loadOverview();
        if (activeMenu === 'cms') loadCms();
        if (activeMenu === 'users') loadUsers(1);
        if (activeMenu === 'bookings') loadBookings(1);
        if (activeMenu === 'support') loadTickets(1, ticketFilter);
    }, [activeMenu, user?.isAdmin, loadOverview, loadCms, loadUsers, loadBookings, loadTickets, ticketFilter]);

    useEffect(() => {
        const query = hotelSearchQuery.trim();
        if (!query || query.length < 2) {
            setHotelSearchResults([]);
            return;
        }

        let cancelled = false;
        const timer = setTimeout(async () => {
            setHotelSearching(true);
            try {
                const res = await api.admin.searchHotels({ query, limit: 6 });
                if (!cancelled && res.data) {
                    setHotelSearchResults(res.data);
                }
            } catch {
                if (!cancelled) setHotelSearchResults([]);
            } finally {
                if (!cancelled) setHotelSearching(false);
            }
        }, 250);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [hotelSearchQuery]);

    const handleSelectHotel = (h: Api.Admin.SearchHotels.HotelItem) => {
        if (!editingItem) return;
        setEditingItem({
            ...editingItem,
            item: {
                ...editingItem.item,
                hid: h.hid,
                name: h.name,
                city: h.city,
                country: h.country,
                rating: h.rating,
                image: h.image,
                regionId: h.regionId,
                bookable: true
            }
        });
        setHotelSearchQuery('');
        setHotelSearchResults([]);
        setSearchingHotel(false);
    };

    // Admin role toggle
    const handleToggleRole = async (targetUser: Api.Admin.Users.UserItem) => {
        try {
            const res = await api.admin.setRole({ userId: targetUser.id, isAdmin: !targetUser.isAdmin });
            if (res.data?.user) {
                setNotice(`Updated role for ${targetUser.name} to ${res.data.user.isAdmin ? 'Admin' : 'Member'}.`);
                loadUsers();
            }
        } catch {
            setNotice('Failed to update user role.');
        }
    };

    // Cancel booking
    const handleCancelBooking = async (partnerOrderId: string) => {
        setCancellingOrderId(partnerOrderId);
        try {
            const res = await api.admin.cancelBooking({ partnerOrderId });
            if (res.data?.cancelled) {
                setNotice(`Booking ${partnerOrderId} cancelled successfully.`);
                loadBookings();
            } else {
                setNotice(`Failed to cancel booking: ${errorMessage(res, 'unknown error')}`);
            }
        } catch {
            setNotice('Network error when cancelling booking.');
        } finally {
            setCancellingOrderId(null);
        }
    };

    // CMS Save item
    const handleSaveCmsItem = async (item: Api.Cms.Item, index: number | null) => {
        if (!cmsSections) return;
        const currentSection = cmsSections[activeCmsTab];
        if (!currentSection) return;

        let newItems = [...currentSection.items];
        if (index === null) {
            newItems.push(item);
        } else {
            newItems[index] = item;
        }

        try {
            const res = await api.cms.update({
                key: activeCmsTab,
                title: currentSection.title,
                subtitle: currentSection.subtitle,
                items: newItems
            });
            if (res.data) {
                setNotice(`CMS ${activeCmsTab.replace(/_/g, ' ')} updated successfully.`);
                setEditingItem(null);
                loadCms();
            }
        } catch {
            setNotice('Failed to update CMS section.');
        }
    };

    // CMS Delete item
    const handleDeleteCmsItem = async (index: number) => {
        if (!cmsSections) return;
        const currentSection = cmsSections[activeCmsTab];
        if (!currentSection) return;

        const newItems = currentSection.items.filter((_, i) => i !== index);
        try {
            const res = await api.cms.update({
                key: activeCmsTab,
                title: currentSection.title,
                subtitle: currentSection.subtitle,
                items: newItems
            });
            if (res.data) {
                setNotice('Item removed from CMS.');
                loadCms();
            }
        } catch {
            setNotice('Failed to delete item.');
        }
    };

    // CMS Reset section
    const handleResetCms = async () => {
        try {
            const res = await api.cms.reset({ key: activeCmsTab });
            if (res.data) {
                setCmsSections(res.data);
                setNotice(`Reset ${activeCmsTab.replace(/_/g, ' ')} to defaults.`);
            }
        } catch {
            setNotice('Failed to reset CMS section.');
        }
    };

    const handleChooseCmsCsv = () => {
        if (typeof document === 'undefined') {
            setNotice('CSV import is available in the web admin.');
            return;
        }
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.csv,text/csv';
        input.onchange = async () => {
            const file = input.files?.[0];
            if (!file) return;
            setCmsImporting(true);
            setCmsImportPreview(null);
            try {
                const csv = await file.text();
                const res = await api.cms.previewImport({ csv, fileName: file.name });
                if (res.data) {
                    setCmsImportSource({ csv, fileName: file.name });
                    setCmsImportPreview(res.data);
                    const firstSection = res.data.sections[0];
                    if (firstSection) setActiveCmsTab(firstSection.key);
                    setNotice(res.data.valid ? 'CSV validated. Review the cards before publishing.' : 'CSV needs attention before it can be published.');
                } else {
                    setNotice(errorMessage(res, 'CSV validation failed.'));
                }
            } catch {
                setNotice('Could not validate the CSV.');
            } finally {
                setCmsImporting(false);
            }
        };
        input.click();
    };

    const handlePublishImportedSection = async (section: Api.Cms.ImportPreview.Section) => {
        if (!section.valid || !cmsImportSource) return;
        setPublishingImportKey(section.key);
        const existing = cmsSections?.[section.key];
        try {
            const res = await api.cms.publishImport({
                csv: cmsImportSource.csv,
                fileName: cmsImportSource.fileName,
                key: section.key,
                title: existing?.title,
                subtitle: existing?.subtitle
            });
            if (res.data) {
                setNotice(`Published Strip ${section.stripNumber} with ${section.items.length} cards.`);
                setCmsImportPreview(current => current ? {
                    ...current,
                    sections: current.sections.filter(item => item.key !== section.key)
                } : null);
                await loadCms();
            } else {
                setNotice(errorMessage(res, `Could not publish Strip ${section.stripNumber}.`));
            }
        } catch {
            setNotice(`Could not publish Strip ${section.stripNumber}.`);
        } finally {
            setPublishingImportKey(null);
        }
    };

    // Support reply
    const handleSendReply = async () => {
        if (!selectedTicket || !replyText.trim()) return;
        setReplying(true);
        try {
            const res = await api.admin.replyTicket({
                ticketId: selectedTicket.ticketId,
                text: replyText.trim()
            });
            if (res.data) {
                setSelectedTicket(res.data);
                setReplyText('');
                loadTickets();
            }
        } catch {
            setNotice('Failed to send support reply.');
        } finally {
            setReplying(false);
        }
    };

    // Support change status
    const handleChangeTicketStatus = async (status: Api.Support.Ticket['status']) => {
        if (!selectedTicket) return;
        try {
            const res = await api.admin.setTicketStatus({
                ticketId: selectedTicket.ticketId,
                status
            });
            if (res.data) {
                setSelectedTicket(res.data);
                setNotice(`Ticket ${selectedTicket.ticketId} marked as ${status}.`);
                loadTickets();
            }
        } catch {
            setNotice('Failed to update ticket status.');
        }
    };

    if (!isMember || !user?.isAdmin) {
        return (
            <Screen>
                <TopNav />
                <View style={[styles.body, compact && styles.bodyCompact, styles.authBody]}>
                    <View style={styles.panel}>
                        <View style={styles.icon}>
                            <Shield size={26} color={colors.brand} />
                        </View>
                        <Text style={styles.title}>Admin Access Required</Text>
                        <Text style={styles.sub}>
                            {!isMember
                                ? 'Sign in with an authorized admin account to access the dashboard.'
                                : `Logged in as ${user?.email || 'user'}, but this account does not have admin permissions.`}
                        </Text>
                        {!isMember ? (
                            <Pressable accessibilityRole="button" onPress={openPrompt} style={styles.cta}>
                                <Text style={styles.ctaText}>Sign in</Text>
                            </Pressable>
                        ) : (
                            <Pressable accessibilityRole="button" onPress={() => router.push('/')} style={styles.cta}>
                                <Text style={styles.ctaText}>Go to Home</Text>
                            </Pressable>
                        )}
                    </View>
                </View>
            </Screen>
        );
    }

    const SearchResultRow = ({
        hotel,
        onSelect
    }: {
        hotel: Api.Admin.SearchHotels.HotelItem;
        onSelect: (h: Api.Admin.SearchHotels.HotelItem) => void;
    }) => {
        const [hovered, setHovered] = useState(false);
        return (
            <Pressable
                onPress={() => onSelect(hotel)}
                onHoverIn={() => setHovered(true)}
                onHoverOut={() => setHovered(false)}
                style={[
                    styles.hotelSearchResultItem,
                    hovered && styles.hotelSearchResultItemHovered
                ]}
            >
                {hotel.image ? (
                    <Image source={{ uri: hotel.image }} style={styles.searchResultThumb} />
                ) : (
                    <View style={styles.searchResultThumbPlaceholder}>
                        <Building2 size={16} color={colors.inkFaint} />
                    </View>
                )}
                <View style={{ flex: 1, gap: 2 }}>
                    <Text style={styles.searchResultName} numberOfLines={1}>
                        {hotel.name}
                    </Text>
                    <Text style={styles.searchResultSub} numberOfLines={1}>
                        {[hotel.city, hotel.country].filter(Boolean).join(', ')} • HID: {hotel.hid}
                    </Text>
                </View>
                {hotel.rating ? (
                    <View style={styles.searchResultRating}>
                        <Star size={10} color={colors.brand} fill={colors.brand} />
                        <Text style={styles.searchResultRatingText}>{hotel.rating}</Text>
                    </View>
                ) : null}
            </Pressable>
        );
    };

    const renderCmsEditModal = () => {
        if (!editingItem) return null;
        const isHotelSection = activeCmsTab !== 'popular_destinations';
        const hasSelectedHotel = Boolean(editingItem.item.hid && editingItem.item.hid > 0);

        return (
            <Modal visible transparent animationType="fade">
                <View style={styles.modalOverlay}>
                    <View style={styles.modalCard}>
                        <View style={styles.modalHeader}>
                            <Text style={styles.modalTitle}>
                                {isHotelSection
                                    ? editingItem.index === null
                                        ? 'Add Hotel Card'
                                        : 'Edit Hotel Card'
                                    : editingItem.index === null
                                    ? 'Add Destination Card'
                                    : 'Edit Destination Card'}
                            </Text>
                            <Pressable onPress={() => setEditingItem(null)}>
                                <X size={20} color={colors.ink} />
                            </Pressable>
                        </View>

                        <ScrollView
                            style={styles.modalForm}
                            contentContainerStyle={{ gap: spacing(1.5), overflow: 'visible' as never }}
                            keyboardShouldPersistTaps="always"
                        >
                            {isHotelSection ? (
                                <>
                                    {hasSelectedHotel && !searchingHotel ? (
                                        <View style={styles.selectedHotelCard}>
                                            {editingItem.item.image ? (
                                                <Image source={{ uri: editingItem.item.image }} style={styles.selectedHotelThumb} />
                                            ) : (
                                                <View style={styles.selectedHotelThumbPlaceholder}>
                                                    <Building2 size={24} color={colors.inkFaint} />
                                                </View>
                                            )}
                                            <View style={styles.selectedHotelInfo}>
                                                <Text style={styles.selectedHotelName} numberOfLines={2}>
                                                    {editingItem.item.name || `Hotel #${editingItem.item.hid}`}
                                                </Text>
                                                <View style={styles.selectedHotelMeta}>
                                                    <MapPin size={12} color={colors.inkFaint} />
                                                    <Text style={styles.selectedHotelLocation} numberOfLines={1}>
                                                        {[editingItem.item.city, editingItem.item.country].filter(Boolean).join(', ') || 'Global'}
                                                    </Text>
                                                </View>
                                                <View style={styles.selectedHotelBadges}>
                                                    <View style={styles.hidTag}>
                                                        <Text style={styles.hidTagText}>HID: {editingItem.item.hid}</Text>
                                                    </View>
                                                    {editingItem.item.rating ? (
                                                        <View style={styles.ratingTag}>
                                                            <Star size={10} color={colors.brand} fill={colors.brand} />
                                                            <Text style={styles.ratingTagText}>{editingItem.item.rating} / 10</Text>
                                                        </View>
                                                    ) : null}
                                                </View>
                                            </View>
                                            <Pressable
                                                onPress={() => {
                                                    setSearchingHotel(true);
                                                    setHotelSearchQuery('');
                                                    setHotelSearchResults([]);
                                                }}
                                                style={styles.changeHotelBtn}
                                            >
                                                <Text style={styles.changeHotelBtnText}>Change</Text>
                                            </Pressable>
                                        </View>
                                    ) : (
                                        <View style={styles.hotelSearchSection}>
                                            <Text style={styles.fieldLabel}>Search Hotel (by Name or HID)</Text>
                                            <View style={styles.hotelSearchAnchor}>
                                                <View
                                                    style={[
                                                        styles.hotelSearchInputContainer,
                                                        isHotelSearchFocused && styles.hotelSearchInputContainerFocused
                                                    ]}
                                                >
                                                    <Search
                                                        size={16}
                                                        color={isHotelSearchFocused ? colors.brand : colors.inkSoft}
                                                        style={{ marginLeft: 10 }}
                                                    />
                                                    <TextInput
                                                        value={hotelSearchQuery}
                                                        onChangeText={setHotelSearchQuery}
                                                        onFocus={() => setIsHotelSearchFocused(true)}
                                                        onBlur={() => setIsHotelSearchFocused(false)}
                                                        placeholder="e.g. Pullman, Conrad, 6362880..."
                                                        placeholderTextColor={colors.inkFaint}
                                                        style={styles.hotelSearchInput}
                                                        autoFocus={editingItem.index === null}
                                                    />
                                                    {hotelSearching ? (
                                                        <ActivityIndicator size="small" color={colors.brand} style={{ marginRight: 10 }} />
                                                    ) : hotelSearchQuery.length > 0 ? (
                                                        <Pressable
                                                            onPress={() => {
                                                                setHotelSearchQuery('');
                                                                setHotelSearchResults([]);
                                                            }}
                                                            style={{ marginRight: 10 }}
                                                        >
                                                            <X size={16} color={colors.inkSoft} />
                                                        </Pressable>
                                                    ) : null}
                                                </View>

                                                {/* Autocomplete Dropdown - absolutely positioned below input */}
                                                {hotelSearchResults.length > 0 ? (
                                                    <View style={styles.hotelSearchResultsDropdown}>
                                                        <ScrollView style={{ maxHeight: 220 }} nestedScrollEnabled keyboardShouldPersistTaps="always">
                                                            {hotelSearchResults.map(h => (
                                                                <SearchResultRow key={h.hid} hotel={h} onSelect={handleSelectHotel} />
                                                            ))}
                                                        </ScrollView>
                                                    </View>
                                                ) : hotelSearchQuery.trim().length >= 2 && !hotelSearching ? (
                                                    <View style={styles.noResultsDropdown}>
                                                        <Text style={styles.noResultsText}>No hotels found matching "{hotelSearchQuery}" in local database.</Text>
                                                    </View>
                                                ) : null}
                                            </View>

                                            {hasSelectedHotel ? (
                                                <Pressable
                                                    onPress={() => setSearchingHotel(false)}
                                                    style={{ alignSelf: 'flex-start', marginTop: 4 }}
                                                >
                                                    <Text style={{ fontFamily: fontFamily.medium, fontSize: 12, color: colors.brand }}>
                                                        ← Keep current hotel ({editingItem.item.name})
                                                    </Text>
                                                </Pressable>
                                            ) : null}
                                        </View>
                                    )}

                                    <View style={{ flexDirection: 'row', gap: 10 }}>
                                        <View style={{ flex: 1 }}>
                                            <Text style={styles.fieldLabel}>Member Price ($)</Text>
                                            <TextInput
                                                value={String(editingItem.item.price ?? '')}
                                                onChangeText={text =>
                                                    setEditingItem({
                                                        ...editingItem,
                                                        item: { ...editingItem.item, price: Number(text) || 0 }
                                                    })
                                                }
                                                keyboardType="numeric"
                                                placeholder="177"
                                                style={styles.formInput}
                                            />
                                        </View>
                                        <View style={{ flex: 1 }}>
                                            <Text style={styles.fieldLabel}>Online Price ($)</Text>
                                            <TextInput
                                                value={String(editingItem.item.guestPrice ?? '')}
                                                onChangeText={text =>
                                                    setEditingItem({
                                                        ...editingItem,
                                                        item: { ...editingItem.item, guestPrice: Number(text) || 0 }
                                                    })
                                                }
                                                keyboardType="numeric"
                                                placeholder="214"
                                                style={styles.formInput}
                                            />
                                        </View>
                                    </View>

                                    <Text style={styles.fieldLabel}>Badge Tag (e.g. "Top Member Rate", "Flash Deal")</Text>
                                    <TextInput
                                        value={editingItem.item.badge ?? ''}
                                        onChangeText={text =>
                                            setEditingItem({
                                                ...editingItem,
                                                item: { ...editingItem.item, badge: text }
                                            })
                                        }
                                        placeholder="e.g. 25% Off"
                                        style={styles.formInput}
                                    />

                                    <Pressable
                                        onPress={() => setShowOverrides(!showOverrides)}
                                        style={styles.overridesToggle}
                                    >
                                        <SlidersHorizontal size={13} color={colors.inkSoft} />
                                        <Text style={styles.overridesToggleText}>
                                            {showOverrides ? 'Hide Custom Overrides' : 'Custom Overrides (Optional)'}
                                        </Text>
                                    </Pressable>

                                    {showOverrides ? (
                                        <View style={styles.overridesBox}>
                                            <Text style={styles.fieldLabel}>Custom Display Title (leave blank to use hotel name)</Text>
                                            <TextInput
                                                value={editingItem.item.name}
                                                onChangeText={text =>
                                                    setEditingItem({
                                                        ...editingItem,
                                                        item: { ...editingItem.item, name: text }
                                                    })
                                                }
                                                placeholder="Auto-populated from hotel"
                                                style={styles.formInput}
                                            />

                                            <Text style={styles.fieldLabel}>Custom Hero Image URL (leave blank to use hotel photo)</Text>
                                            <TextInput
                                                value={editingItem.item.image}
                                                onChangeText={text =>
                                                    setEditingItem({
                                                        ...editingItem,
                                                        item: { ...editingItem.item, image: text }
                                                    })
                                                }
                                                placeholder="Auto-populated from hotel"
                                                style={styles.formInput}
                                            />
                                        </View>
                                    ) : null}
                                </>
                            ) : (
                                <>
                                    <Text style={styles.fieldLabel}>Destination / City Name</Text>
                                    <TextInput
                                        value={editingItem.item.name}
                                        onChangeText={text =>
                                            setEditingItem({
                                                ...editingItem,
                                                item: { ...editingItem.item, name: text }
                                            })
                                        }
                                        placeholder="e.g. Tokyo"
                                        style={styles.formInput}
                                    />

                                    <View style={{ flexDirection: 'row', gap: 10 }}>
                                        <View style={{ flex: 1 }}>
                                            <Text style={styles.fieldLabel}>City / State</Text>
                                            <TextInput
                                                value={editingItem.item.city ?? ''}
                                                onChangeText={text =>
                                                    setEditingItem({
                                                        ...editingItem,
                                                        item: { ...editingItem.item, city: text }
                                                    })
                                                }
                                                placeholder="e.g. Tokyo Prefecture"
                                                style={styles.formInput}
                                            />
                                        </View>
                                        <View style={{ flex: 1 }}>
                                            <Text style={styles.fieldLabel}>Country</Text>
                                            <TextInput
                                                value={editingItem.item.country ?? ''}
                                                onChangeText={text =>
                                                    setEditingItem({
                                                        ...editingItem,
                                                        item: { ...editingItem.item, country: text }
                                                    })
                                                }
                                                placeholder="e.g. Japan"
                                                style={styles.formInput}
                                            />
                                        </View>
                                    </View>

                                    <View style={{ flexDirection: 'row', gap: 10 }}>
                                        <View style={{ flex: 1 }}>
                                            <Text style={styles.fieldLabel}>Starting Price ($)</Text>
                                            <TextInput
                                                value={String(editingItem.item.price ?? '')}
                                                onChangeText={text =>
                                                    setEditingItem({
                                                        ...editingItem,
                                                        item: { ...editingItem.item, price: Number(text) || 0 }
                                                    })
                                                }
                                                keyboardType="numeric"
                                                placeholder="95"
                                                style={styles.formInput}
                                            />
                                        </View>
                                        <View style={{ flex: 1 }}>
                                            <Text style={styles.fieldLabel}>Online Price ($)</Text>
                                            <TextInput
                                                value={String(editingItem.item.guestPrice ?? '')}
                                                onChangeText={text =>
                                                    setEditingItem({
                                                        ...editingItem,
                                                        item: { ...editingItem.item, guestPrice: Number(text) || 0 }
                                                    })
                                                }
                                                keyboardType="numeric"
                                                placeholder="140"
                                                style={styles.formInput}
                                            />
                                        </View>
                                    </View>

                                    <Text style={styles.fieldLabel}>Badge Tag (e.g. "Trending", "Popular")</Text>
                                    <TextInput
                                        value={editingItem.item.badge ?? ''}
                                        onChangeText={text =>
                                            setEditingItem({
                                                ...editingItem,
                                                item: { ...editingItem.item, badge: text }
                                            })
                                        }
                                        placeholder="e.g. Trending"
                                        style={styles.formInput}
                                    />

                                    <Text style={styles.fieldLabel}>Image URL</Text>
                                    <TextInput
                                        value={editingItem.item.image}
                                        onChangeText={text =>
                                            setEditingItem({
                                                ...editingItem,
                                                item: { ...editingItem.item, image: text }
                                            })
                                        }
                                        placeholder="https://picsum.photos/seed/.../720/480"
                                        style={styles.formInput}
                                    />

                                    <Text style={styles.fieldLabel}>Region ID (for Search click)</Text>
                                    <TextInput
                                        value={String(editingItem.item.regionId ?? 0)}
                                        onChangeText={text =>
                                            setEditingItem({
                                                ...editingItem,
                                                item: { ...editingItem.item, regionId: Number(text) || 0 }
                                            })
                                        }
                                        keyboardType="numeric"
                                        placeholder="3593"
                                        style={styles.formInput}
                                    />
                                </>
                            )}
                        </ScrollView>

                        <View style={styles.modalFooter}>
                            <Pressable onPress={() => setEditingItem(null)} style={styles.secondaryActionBtn}>
                                <Text style={styles.secondaryActionText}>Cancel</Text>
                            </Pressable>
                            <Pressable
                                onPress={() => handleSaveCmsItem(editingItem.item, editingItem.index)}
                                style={[
                                    styles.primaryActionBtn,
                                    isHotelSection && !hasSelectedHotel && !editingItem.item.name && { opacity: 0.5 }
                                ]}
                                disabled={Boolean(isHotelSection && !hasSelectedHotel && !editingItem.item.name)}
                            >
                                <Text style={styles.primaryActionText}>Save Changes</Text>
                            </Pressable>
                        </View>
                    </View>
                </View>
            </Modal>
        );
    };

    return (
        <Screen>
            <TopNav />

            <View style={[styles.body, compact && styles.bodyCompact]}>
                {/* Header & Menu Bar */}
                <View style={[styles.header, compact && styles.headerCompact]}>
                    <View style={styles.headerCopy}>
                        <Text style={[styles.heading, compact && styles.headingCompact]}>Admin Dashboard</Text>
                    </View>
                    <Pressable
                        accessibilityLabel="Refresh admin data"
                        accessibilityRole="button"
                        onPress={() => {
                            if (activeMenu === 'overview') loadOverview();
                            if (activeMenu === 'cms') loadCms();
                            if (activeMenu === 'users') loadUsers();
                            if (activeMenu === 'bookings') loadBookings();
                            if (activeMenu === 'support') loadTickets();
                        }}
                        style={[styles.refreshBtn, compact && styles.refreshBtnCompact]}
                    >
                        <RefreshCw size={15} color={colors.inkSoft} />
                        {!compact ? <Text style={styles.refreshText}>Refresh</Text> : null}
                    </Pressable>
                </View>

                {notice ? (
                    <View style={styles.noticeBar}>
                        <Text style={styles.noticeText}>{notice}</Text>
                        <Pressable onPress={() => setNotice(null)}>
                            <X size={16} color={colors.brand} />
                        </Pressable>
                    </View>
                ) : null}

                {/* Main Navigation Menus */}
                <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    style={styles.menuNavScroll}
                    contentContainerStyle={styles.menuNav}
                >
                    <Pressable
                        accessibilityRole="button"
                        onPress={() => setActiveMenu('overview')}
                        style={[styles.menuItem, activeMenu === 'overview' && styles.menuItemActive]}
                    >
                        <LayoutDashboard size={17} color={activeMenu === 'overview' ? colors.brand : colors.inkSoft} />
                        <Text style={[styles.menuItemText, activeMenu === 'overview' && styles.menuItemTextActive]}>
                            Overview
                        </Text>
                    </Pressable>

                    <Pressable
                        accessibilityRole="button"
                        onPress={() => setActiveMenu('cms')}
                        style={[styles.menuItem, activeMenu === 'cms' && styles.menuItemActive]}
                    >
                        <Layers size={17} color={activeMenu === 'cms' ? colors.brand : colors.inkSoft} />
                        <Text style={[styles.menuItemText, activeMenu === 'cms' && styles.menuItemTextActive]}>
                            CMS (Home Merch)
                        </Text>
                    </Pressable>

                    <Pressable
                        accessibilityRole="button"
                        onPress={() => setActiveMenu('users')}
                        style={[styles.menuItem, activeMenu === 'users' && styles.menuItemActive]}
                    >
                        <Users size={17} color={activeMenu === 'users' ? colors.brand : colors.inkSoft} />
                        <Text style={[styles.menuItemText, activeMenu === 'users' && styles.menuItemTextActive]}>
                            Users
                        </Text>
                    </Pressable>

                    <Pressable
                        accessibilityRole="button"
                        onPress={() => setActiveMenu('bookings')}
                        style={[styles.menuItem, activeMenu === 'bookings' && styles.menuItemActive]}
                    >
                        <Building2 size={17} color={activeMenu === 'bookings' ? colors.brand : colors.inkSoft} />
                        <Text style={[styles.menuItemText, activeMenu === 'bookings' && styles.menuItemTextActive]}>
                            Trips & Bookings
                        </Text>
                    </Pressable>

                    <Pressable
                        accessibilityRole="button"
                        onPress={() => setActiveMenu('support')}
                        style={[styles.menuItem, activeMenu === 'support' && styles.menuItemActive]}
                    >
                        <MessageSquare size={17} color={activeMenu === 'support' ? colors.brand : colors.inkSoft} />
                        <Text style={[styles.menuItemText, activeMenu === 'support' && styles.menuItemTextActive]}>
                            Support Tickets
                        </Text>
                    </Pressable>
                </ScrollView>

                {/* 1. OVERVIEW VIEW */}
                {activeMenu === 'overview' ? (
                    <View style={styles.viewContent}>
                        {overviewLoading && !overviewData ? (
                            <ActivityIndicator color={colors.brand} style={{ marginVertical: spacing(4) }} />
                        ) : (
                            <>
                                <View style={styles.statsGrid}>
                                    <View style={styles.statCard}>
                                        <View style={styles.statHeader}>
                                            <Users size={18} color={colors.brand} />
                                            <Text style={styles.statLabel}>Total Users</Text>
                                        </View>
                                        <Text style={styles.statValue}>{overviewData?.metrics.totalUsers ?? 0}</Text>
                                    </View>
                                    <View style={styles.statCard}>
                                        <View style={styles.statHeader}>
                                            <Building2 size={18} color={colors.green} />
                                            <Text style={styles.statLabel}>Total Bookings</Text>
                                        </View>
                                        <Text style={styles.statValue}>{overviewData?.metrics.totalBookings ?? 0}</Text>
                                    </View>
                                    <View style={styles.statCard}>
                                        <View style={styles.statHeader}>
                                            <MessageSquare size={18} color={colors.indigo} />
                                            <Text style={styles.statLabel}>Active Tickets</Text>
                                        </View>
                                        <Text style={styles.statValue}>{overviewData?.metrics.activeTickets ?? 0}</Text>
                                    </View>
                                    <View style={styles.statCard}>
                                        <View style={styles.statHeader}>
                                            <Layers size={18} color={colors.pink} />
                                            <Text style={styles.statLabel}>CMS Rails</Text>
                                        </View>
                                        <Text style={styles.statValue}>3 Active</Text>
                                    </View>
                                </View>

                                <View style={[styles.twoCol, compact && styles.oneCol]}>
                                    <View style={styles.sectionCard}>
                                        <View style={styles.cardHeader}>
                                            <Text style={styles.cardTitle}>Recent Users</Text>
                                            <Pressable onPress={() => setActiveMenu('users')}>
                                                <Text style={styles.linkText}>View all</Text>
                                            </Pressable>
                                        </View>
                                        <View style={styles.cardList}>
                                            {overviewData?.recentUsers.map(u => (
                                                <View key={u.id} style={styles.overviewRow}>
                                                    <View style={styles.avatar}>
                                                        <Text style={styles.avatarText}>{u.name.charAt(0).toUpperCase()}</Text>
                                                    </View>
                                                    <View style={{ flex: 1 }}>
                                                        <Text style={styles.rowTitle}>{u.name}</Text>
                                                        <Text style={styles.rowSub}>{u.email}</Text>
                                                    </View>
                                                    {u.isAdmin ? (
                                                        <View style={styles.roleBadge}><Text style={styles.roleText}>Admin</Text></View>
                                                    ) : null}
                                                </View>
                                            ))}
                                        </View>
                                    </View>

                                    <View style={styles.sectionCard}>
                                        <View style={styles.cardHeader}>
                                            <Text style={styles.cardTitle}>Recent Support Tickets</Text>
                                            <Pressable onPress={() => setActiveMenu('support')}>
                                                <Text style={styles.linkText}>View queue</Text>
                                            </Pressable>
                                        </View>
                                        <View style={styles.cardList}>
                                            {overviewData?.recentTickets.map(t => (
                                                <Pressable
                                                    key={t.ticketId}
                                                    onPress={() => {
                                                        setActiveMenu('support');
                                                    }}
                                                    style={styles.overviewRow}
                                                >
                                                    <View style={{ flex: 1 }}>
                                                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                                                            <Text style={styles.rowTitle}>{t.ticketId}</Text>
                                                            <StatusPill status={t.status} />
                                                        </View>
                                                        <Text style={styles.rowSub} numberOfLines={1}>{t.subject}</Text>
                                                    </View>
                                                    <Text style={styles.rowTime}>{t.userName}</Text>
                                                </Pressable>
                                            ))}
                                        </View>
                                    </View>
                                </View>
                            </>
                        )}
                    </View>
                ) : null}

                {/* 2. CMS VIEW */}
                {activeMenu === 'cms' ? (
                    <View style={styles.viewContent}>
                        <View style={[styles.cmsHeader, compact && styles.cmsHeaderCompact]}>
                            <ScrollView
                                horizontal
                                showsHorizontalScrollIndicator={false}
                                style={[styles.cmsSubNavScroll, compact && styles.cmsSubNavScrollCompact]}
                                contentContainerStyle={styles.cmsSubNav}
                            >
                                <Pressable
                                    onPress={() => setActiveCmsTab('best_deals')}
                                    style={[styles.cmsSubTab, activeCmsTab === 'best_deals' && styles.cmsSubTabActive]}
                                >
                                    <Sparkles size={14} color={activeCmsTab === 'best_deals' ? colors.brand : colors.inkSoft} />
                                    <Text style={[styles.cmsSubTabText, activeCmsTab === 'best_deals' && styles.cmsSubTabTextActive]}>
                                        Strip 1 · Today’s prices
                                    </Text>
                                </Pressable>
                                <Pressable
                                    onPress={() => setActiveCmsTab('best_value_hotels')}
                                    style={[styles.cmsSubTab, activeCmsTab === 'best_value_hotels' && styles.cmsSubTabActive]}
                                >
                                    <Tag size={14} color={activeCmsTab === 'best_value_hotels' ? colors.brand : colors.inkSoft} />
                                    <Text style={[styles.cmsSubTabText, activeCmsTab === 'best_value_hotels' && styles.cmsSubTabTextActive]}>
                                        Strip 2 · Best value
                                    </Text>
                                </Pressable>
                                <Pressable
                                    onPress={() => setActiveCmsTab('popular_destinations')}
                                    style={[styles.cmsSubTab, activeCmsTab === 'popular_destinations' && styles.cmsSubTabActive]}
                                >
                                    <Globe size={14} color={activeCmsTab === 'popular_destinations' ? colors.brand : colors.inkSoft} />
                                    <Text style={[styles.cmsSubTabText, activeCmsTab === 'popular_destinations' && styles.cmsSubTabTextActive]}>
                                        Popular Destinations
                                    </Text>
                                </Pressable>
                                <Pressable
                                    onPress={() => setActiveCmsTab('hot_picks')}
                                    style={[styles.cmsSubTab, activeCmsTab === 'hot_picks' && styles.cmsSubTabActive]}
                                >
                                    <Flame size={14} color={activeCmsTab === 'hot_picks' ? colors.brand : colors.inkSoft} />
                                    <Text style={[styles.cmsSubTabText, activeCmsTab === 'hot_picks' && styles.cmsSubTabTextActive]}>
                                        Strip 3 · Luxury
                                    </Text>
                                </Pressable>
                            </ScrollView>

                            <View style={[styles.cmsActions, compact && styles.cmsActionsCompact]}>
                                <Pressable onPress={handleChooseCmsCsv} disabled={cmsImporting} style={styles.secondaryActionBtn}>
                                    {cmsImporting ? <ActivityIndicator size="small" color={colors.brand} /> : <Upload size={14} color={colors.brand} />}
                                    <Text style={[styles.secondaryActionText, { color: colors.brand }]}>Upload CSV</Text>
                                </Pressable>
                                <Pressable onPress={handleResetCms} style={styles.secondaryActionBtn}>
                                    <RotateCcw size={14} color={colors.inkSoft} />
                                    <Text style={styles.secondaryActionText}>Reset Defaults</Text>
                                </Pressable>
                                <Pressable
                                    onPress={() => {
                                        const isHotelSection = activeCmsTab !== 'popular_destinations';
                                        setSearchingHotel(isHotelSection);
                                        setHotelSearchQuery('');
                                        setHotelSearchResults([]);
                                        setShowOverrides(false);
                                        setEditingItem({
                                            index: null,
                                            item: isHotelSection
                                                ? {
                                                      id: `hotel_${Date.now()}`,
                                                      hid: 0,
                                                      name: '',
                                                      city: '',
                                                      country: '',
                                                      price: 150,
                                                      guestPrice: 200,
                                                      rating: 0,
                                                      image: '',
                                                      badge: '',
                                                      bookable: true
                                                  }
                                                : {
                                                      id: `dest_${Date.now()}`,
                                                      hid: 0,
                                                      regionId: 0,
                                                      name: '',
                                                      city: '',
                                                      country: '',
                                                      price: 100,
                                                      guestPrice: 150,
                                                      rating: 4.8,
                                                      image: 'https://picsum.photos/seed/dest/720/480',
                                                      badge: 'Trending',
                                                      bookable: false
                                                  }
                                        });
                                    }}
                                    style={styles.primaryActionBtn}
                                >
                                    <Plus size={15} color={colors.white} />
                                    <Text style={styles.primaryActionText}>
                                        {activeCmsTab === 'popular_destinations' ? 'Add Destination' : 'Add Hotel Card'}
                                    </Text>
                                </Pressable>
                            </View>
                        </View>

                        {cmsImportPreview ? (
                            <View style={styles.importPanel}>
                                <View style={styles.importPanelHeader}>
                                    <View style={{ flex: 1, gap: 3 }}>
                                        <Text style={styles.importTitle}>{cmsImportPreview.fileName}</Text>
                                        <Text style={styles.importSubtitle}>{cmsImportPreview.rowCount} CSV rows checked</Text>
                                    </View>
                                    <View style={[styles.importStatus, cmsImportPreview.valid ? styles.importStatusValid : styles.importStatusInvalid]}>
                                        <Text style={[styles.importStatusText, cmsImportPreview.valid ? styles.importStatusTextValid : styles.importStatusTextInvalid]}>
                                            {cmsImportPreview.valid ? 'Ready to publish' : 'Needs attention'}
                                        </Text>
                                    </View>
                                    <Pressable accessibilityLabel="Close CSV preview" onPress={() => { setCmsImportPreview(null); setCmsImportSource(null); }} style={styles.deleteBtn}>
                                        <X size={17} color={colors.inkFaint} />
                                    </Pressable>
                                </View>

                                {cmsImportPreview.issues.map((entry, index) => (
                                    <Text key={`${entry.code}-${index}`} style={entry.severity === 'error' ? styles.importErrorText : styles.importWarningText}>
                                        {entry.row ? `Row ${entry.row}: ` : ''}{entry.message}
                                    </Text>
                                ))}

                                {cmsImportPreview.sections.map(section => {
                                    const errors = section.issues.filter(entry => entry.severity === 'error');
                                    const warnings = section.issues.filter(entry => entry.severity === 'warning');
                                    const publishing = publishingImportKey === section.key;
                                    const previewHotels = section.items.flatMap(item => toCmsHotel(item) ?? []);
                                    return (
                                        <View key={section.key} style={styles.importSection}>
                                        <View style={styles.importSectionRow}>
                                            <View style={{ flex: 1, gap: 4 }}>
                                                <Text style={styles.importSectionTitle}>Strip {section.stripNumber} · {section.items.length} cards</Text>
                                                <Text style={styles.importSectionMeta}>
                                                    {errors.length ? `${errors.length} errors` : 'All rules passed'}{warnings.length ? ` · ${warnings.length} warnings` : ''}
                                                </Text>
                                                {section.issues.slice(0, 6).map((entry, index) => (
                                                    <Text key={`${entry.code}-${entry.row ?? index}`} style={entry.severity === 'error' ? styles.importErrorText : styles.importWarningText}>
                                                        {entry.row ? `Row ${entry.row}: ` : ''}{entry.message}
                                                    </Text>
                                                ))}
                                            </View>
                                            <Pressable
                                                disabled={!section.valid || publishing}
                                                onPress={() => handlePublishImportedSection(section)}
                                                style={[styles.primaryActionBtn, (!section.valid || publishing) && styles.importPublishDisabled]}
                                            >
                                                {publishing ? <ActivityIndicator size="small" color={colors.white} /> : null}
                                                <Text style={styles.primaryActionText}>Publish Strip {section.stripNumber}</Text>
                                            </Pressable>
                                        </View>
                                        {/* Same cards the homepage draws, so what is published is what is seen here. */}
                                        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.importPreviewRow}>
                                            {previewHotels.map(hotel => (
                                                section.stripNumber === 1
                                                    ? <SelectRateCard key={hotel.id} hotel={hotel} cardWidth={300} compact={false} />
                                                    : <CollectionHotelCard key={hotel.id} hotel={hotel} onPress={() => undefined} />
                                            ))}
                                        </ScrollView>
                                        </View>
                                    );
                                })}
                            </View>
                        ) : null}

                        {cmsLoading && !cmsSections ? (
                            <ActivityIndicator color={colors.brand} style={{ marginVertical: spacing(4) }} />
                        ) : (
                            <View style={styles.cmsGrid}>
                                {cmsSections?.[activeCmsTab]?.items.map((item, idx) => (
                                    <View key={item.id || idx} style={styles.cmsCard}>
                                        {item.image ? (
                                            <Image source={{ uri: item.image }} style={styles.cmsImage} />
                                        ) : (
                                            <View style={styles.cmsImagePlaceholder} />
                                        )}
                                        <View style={styles.cmsCardBody}>
                                            <View style={styles.cmsCardTitleRow}>
                                                <Text style={styles.cmsCardTitle} numberOfLines={1}>{item.name}</Text>
                                                {item.badge ? (
                                                    <View style={styles.cmsBadge}>
                                                        <Text style={styles.cmsBadgeText}>{item.badge}</Text>
                                                    </View>
                                                ) : null}
                                            </View>
                                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                                <Text style={styles.cmsCardSub}>
                                                    {[item.city, item.country].filter(Boolean).join(', ')}
                                                </Text>
                                                {item.hid ? (
                                                    <View style={styles.cardHidBadge}>
                                                        <Text style={styles.cardHidBadgeText}>HID: {item.hid}</Text>
                                                    </View>
                                                ) : null}
                                                {item.rating ? (
                                                    <View style={styles.cardRatingBadge}>
                                                        <Star size={9} color={colors.brand} fill={colors.brand} />
                                                        <Text style={styles.cardRatingText}>{item.rating}</Text>
                                                    </View>
                                                ) : null}
                                            </View>
                                            {item.price ? (
                                                <View style={styles.priceRow}>
                                                    <Text style={styles.cmsPrice}>${item.price}</Text>
                                                    {item.guestPrice ? (
                                                        <Text style={styles.cmsGuestPrice}>Online: ${item.guestPrice}</Text>
                                                    ) : null}
                                                </View>
                                            ) : null}

                                            <View style={styles.cmsCardActions}>
                                                <Pressable
                                                    onPress={() => {
                                                        setEditingItem({ index: idx, item: { ...item } });
                                                        setSearchingHotel(!item.hid);
                                                        setHotelSearchQuery('');
                                                        setHotelSearchResults([]);
                                                        setShowOverrides(false);
                                                    }}
                                                    style={styles.editBtn}
                                                >
                                                    <Text style={styles.editBtnText}>Edit</Text>
                                                </Pressable>
                                                <Pressable
                                                    onPress={() => handleDeleteCmsItem(idx)}
                                                    style={styles.deleteBtn}
                                                >
                                                    <Trash2 size={14} color={colors.pink} />
                                                </Pressable>
                                            </View>
                                        </View>
                                    </View>
                                ))}
                            </View>
                        )}
                    </View>
                ) : null}

                {/* 3. USERS VIEW */}
                {activeMenu === 'users' ? (
                    <View style={styles.viewContent}>
                        <View style={styles.toolbar}>
                            <TextInput
                                value={userSearch}
                                onChangeText={text => {
                                    setUserSearch(text);
                                    loadUsers(1, text);
                                }}
                                placeholder="Search users by name or email…"
                                placeholderTextColor={colors.inkFaint}
                                style={styles.searchInput}
                            />
                        </View>

                        {usersLoading && !usersList ? (
                            <ActivityIndicator color={colors.brand} style={{ marginVertical: spacing(4) }} />
                        ) : (
                            <>
                                <View style={styles.tableCard}>
                                    <View style={styles.tableHeader}>
                                        <Text style={[styles.th, { flex: 2 }]}>User</Text>
                                        <Text style={[styles.th, { flex: 1 }]}>Role</Text>
                                        <Text style={[styles.th, { flex: 1 }]}>Bookings</Text>
                                        <Text style={[styles.th, { flex: 1, textAlign: 'right' }]}>Action</Text>
                                    </View>
                                    {usersList?.map(u => (
                                        <View key={u.id} style={styles.tableRow}>
                                            <View style={[styles.td, { flex: 2, flexDirection: 'row', alignItems: 'center', gap: 10 }]}>
                                                <View style={styles.avatar}>
                                                    <Text style={styles.avatarText}>{u.name.charAt(0).toUpperCase()}</Text>
                                                </View>
                                                <View>
                                                    <Text style={styles.userName}>{u.name}</Text>
                                                    <Text style={styles.userEmail}>{u.email}</Text>
                                                </View>
                                            </View>
                                            <View style={[styles.td, { flex: 1 }]}>
                                                <View style={[styles.rolePill, u.isAdmin ? styles.roleAdmin : styles.roleMember]}>
                                                    <Text style={[styles.rolePillText, u.isAdmin ? styles.roleAdminText : styles.roleMemberText]}>
                                                        {u.isAdmin ? 'Admin' : 'Member'}
                                                    </Text>
                                                </View>
                                            </View>
                                            <View style={[styles.td, { flex: 1 }]}>
                                                <Text style={styles.cellText}>{u.bookingCount} trips</Text>
                                            </View>
                                            <View style={[styles.td, { flex: 1, alignItems: 'flex-end' }]}>
                                                <Pressable
                                                    onPress={() => handleToggleRole(u)}
                                                    style={styles.actionToggleBtn}
                                                >
                                                    <Text style={styles.actionToggleText}>
                                                        {u.isAdmin ? 'Revoke Admin' : 'Make Admin'}
                                                    </Text>
                                                </Pressable>
                                            </View>
                                        </View>
                                    ))}
                                    {usersList?.length === 0 ? (
                                        <View style={{ padding: spacing(3), alignItems: 'center' }}>
                                            <Text style={styles.emptySub}>No users found.</Text>
                                        </View>
                                    ) : null}
                                </View>

                                {usersTotalPages > 1 ? (
                                    <View style={styles.paginationCard}>
                                        <Text style={styles.paginationInfo}>
                                            Showing page {usersPage} of {usersTotalPages} ({usersTotal} users total)
                                        </Text>
                                        <View style={styles.paginationActions}>
                                            <Pressable
                                                disabled={usersPage <= 1 || usersLoading}
                                                onPress={() => loadUsers(usersPage - 1)}
                                                style={[styles.pageBtn, (usersPage <= 1 || usersLoading) && styles.pageBtnDisabled]}
                                            >
                                                <ChevronLeft size={16} color={usersPage <= 1 ? colors.inkFaint : colors.ink} />
                                                <Text style={[styles.pageBtnText, (usersPage <= 1 || usersLoading) && styles.pageBtnTextDisabled]}>Previous</Text>
                                            </Pressable>
                                            <Pressable
                                                disabled={usersPage >= usersTotalPages || usersLoading}
                                                onPress={() => loadUsers(usersPage + 1)}
                                                style={[styles.pageBtn, (usersPage >= usersTotalPages || usersLoading) && styles.pageBtnDisabled]}
                                            >
                                                <Text style={[styles.pageBtnText, (usersPage >= usersTotalPages || usersLoading) && styles.pageBtnTextDisabled]}>Next</Text>
                                                <ChevronRight size={16} color={usersPage >= usersTotalPages ? colors.inkFaint : colors.ink} />
                                            </Pressable>
                                        </View>
                                    </View>
                                ) : null}
                            </>
                        )}
                    </View>
                ) : null}

                {/* 4. BOOKINGS VIEW */}
                {activeMenu === 'bookings' ? (
                    <View style={styles.viewContent}>
                        {bookingsLoading && !bookingsList ? (
                            <ActivityIndicator color={colors.brand} style={{ marginVertical: spacing(4) }} />
                        ) : bookingsList?.length === 0 ? (
                            <View style={styles.emptyCard}>
                                <Building2 size={24} color={colors.inkFaint} />
                                <Text style={styles.emptyTitle}>No bookings in system</Text>
                                <Text style={styles.emptySub}>Bookings made by travelers will appear here.</Text>
                            </View>
                        ) : (
                            <>
                                <View style={styles.bookingsList}>
                                    {bookingsList?.map(b => {
                                        const cancelled = b.status === 'cancelled';
                                        const busy = cancellingOrderId === b.partnerOrderId;
                                        return (
                                            <View key={b.bookingDocId || b.partnerOrderId} style={styles.bookingCard}>
                                                {resolveThumbnailSource(b.thumbnailUrl) ? (
                                                    <Image source={resolveThumbnailSource(b.thumbnailUrl)!} style={styles.bookingThumb} />
                                                ) : (
                                                    <View style={styles.bookingThumbPlaceholder} />
                                                )}
                                                <View style={styles.bookingInfo}>
                                                    <View style={styles.bookingMetaRow}>
                                                        <StatusPill status={b.status} />
                                                        <Text style={styles.bookingIdText}>Order #{b.orderId}</Text>
                                                        <Text style={styles.bookingRefText}>({b.partnerOrderId})</Text>
                                                    </View>
                                                    <Text style={styles.bookingHotelName}>{b.hotelName}</Text>
                                                    <Text style={styles.bookingCustomer}>
                                                        Guest: <Text style={{ fontFamily: fontFamily.semibold }}>{b.userName}</Text> ({b.userEmail})
                                                    </Text>
                                                    <View style={styles.bookingDatesRow}>
                                                        <CalendarDays size={13} color={colors.inkFaint} />
                                                        <Text style={styles.bookingDates}>
                                                            {b.checkIn} → {b.checkOut} · {b.roomCount} room(s) · {b.guestCount} guest(s)
                                                        </Text>
                                                    </View>
                                                </View>

                                                <View style={styles.bookingActions}>
                                                    <Text style={styles.bookingPrice}>
                                                        {formatMoney(b.total)}
                                                    </Text>
                                                    {!cancelled && b.isCancellable ? (
                                                        <Pressable
                                                            onPress={() => handleCancelBooking(b.partnerOrderId)}
                                                            disabled={busy}
                                                            style={[styles.cancelBtn, busy && styles.cancelBtnBusy]}
                                                        >
                                                            {busy ? (
                                                                <ActivityIndicator size="small" color={colors.pink} />
                                                            ) : (
                                                                <Text style={styles.cancelBtnText}>Cancel Booking</Text>
                                                            )}
                                                        </Pressable>
                                                    ) : !cancelled ? (
                                                        <Text style={styles.nonRefundableText}>Non-refundable</Text>
                                                    ) : null}
                                                </View>
                                            </View>
                                        );
                                    })}
                                </View>

                                {bookingsTotalPages > 1 ? (
                                    <View style={styles.paginationCard}>
                                        <Text style={styles.paginationInfo}>
                                            Showing page {bookingsPage} of {bookingsTotalPages} ({bookingsTotal} bookings total)
                                        </Text>
                                        <View style={styles.paginationActions}>
                                            <Pressable
                                                disabled={bookingsPage <= 1 || bookingsLoading}
                                                onPress={() => loadBookings(bookingsPage - 1)}
                                                style={[styles.pageBtn, (bookingsPage <= 1 || bookingsLoading) && styles.pageBtnDisabled]}
                                            >
                                                <ChevronLeft size={16} color={bookingsPage <= 1 ? colors.inkFaint : colors.ink} />
                                                <Text style={[styles.pageBtnText, (bookingsPage <= 1 || bookingsLoading) && styles.pageBtnTextDisabled]}>Previous</Text>
                                            </Pressable>
                                            <Pressable
                                                disabled={bookingsPage >= bookingsTotalPages || bookingsLoading}
                                                onPress={() => loadBookings(bookingsPage + 1)}
                                                style={[styles.pageBtn, (bookingsPage >= bookingsTotalPages || bookingsLoading) && styles.pageBtnDisabled]}
                                            >
                                                <Text style={[styles.pageBtnText, (bookingsPage >= bookingsTotalPages || bookingsLoading) && styles.pageBtnTextDisabled]}>Next</Text>
                                                <ChevronRight size={16} color={bookingsPage >= bookingsTotalPages ? colors.inkFaint : colors.ink} />
                                            </Pressable>
                                        </View>
                                    </View>
                                ) : null}
                            </>
                        )}
                    </View>
                ) : null}

                {/* 5. SUPPORT TICKETS VIEW */}
                {activeMenu === 'support' ? (
                    <View style={styles.viewContent}>
                        <View style={styles.ticketFilters}>
                            {(['all', 'open', 'in_progress', 'resolved', 'closed'] as const).map(f => (
                                <Pressable
                                    key={f}
                                    onPress={() => {
                                        setTicketFilter(f);
                                        loadTickets(1, f);
                                    }}
                                    style={[styles.filterPill, ticketFilter === f && styles.filterPillActive]}
                                >
                                    <Text style={[styles.filterPillText, ticketFilter === f && styles.filterPillTextActive]}>
                                        {f.replace('_', ' ').toUpperCase()}
                                    </Text>
                                </Pressable>
                            ))}
                        </View>

                        <View style={[styles.twoCol, compact && styles.oneCol]}>
                            {/* Tickets Queue */}
                            <View style={styles.ticketQueueCard}>
                                <Text style={styles.queueHeader}>Conversation Queue</Text>
                                {supportLoading && !ticketsList ? (
                                    <ActivityIndicator color={colors.brand} style={{ marginVertical: spacing(2) }} />
                                ) : (
                                    <>
                                        <View style={styles.queueList}>
                                            {ticketsList?.map(t => {
                                                const selected = selectedTicket?.ticketId === t.ticketId;
                                                return (
                                                    <Pressable
                                                        key={t.ticketId}
                                                        onPress={() => setSelectedTicket(t)}
                                                        style={[styles.ticketRow, selected && styles.ticketRowActive]}
                                                    >
                                                        <View style={styles.ticketRowHeader}>
                                                            <Text style={styles.ticketId}>{t.ticketId}</Text>
                                                            <StatusPill status={t.status} />
                                                        </View>
                                                        <Text style={styles.ticketSubject} numberOfLines={1}>{t.subject}</Text>
                                                        <Text style={styles.ticketUser} numberOfLines={1}>
                                                            {t.userName} · {t.bookingId ? `Booking #${t.bookingId}` : 'General Inquiry'}
                                                        </Text>
                                                    </Pressable>
                                                );
                                            })}
                                            {ticketsList?.length === 0 ? (
                                                <Text style={styles.emptySub}>No support tickets found.</Text>
                                            ) : null}
                                        </View>

                                        {ticketsTotalPages > 1 ? (
                                            <View style={[styles.paginationCard, { marginTop: spacing(1.5), padding: spacing(1) }]}>
                                                <Text style={styles.paginationInfo}>
                                                    Page {ticketsPage} of {ticketsTotalPages} ({ticketsTotal})
                                                </Text>
                                                <View style={styles.paginationActions}>
                                                    <Pressable
                                                        disabled={ticketsPage <= 1 || supportLoading}
                                                        onPress={() => loadTickets(ticketsPage - 1)}
                                                        style={[styles.pageBtn, (ticketsPage <= 1 || supportLoading) && styles.pageBtnDisabled]}
                                                    >
                                                        <ChevronLeft size={14} color={ticketsPage <= 1 ? colors.inkFaint : colors.ink} />
                                                    </Pressable>
                                                    <Pressable
                                                        disabled={ticketsPage >= ticketsTotalPages || supportLoading}
                                                        onPress={() => loadTickets(ticketsPage + 1)}
                                                        style={[styles.pageBtn, (ticketsPage >= ticketsTotalPages || supportLoading) && styles.pageBtnDisabled]}
                                                    >
                                                        <ChevronRight size={14} color={ticketsPage >= ticketsTotalPages ? colors.inkFaint : colors.ink} />
                                                    </Pressable>
                                                </View>
                                            </View>
                                        ) : null}
                                    </>
                                )}
                            </View>

                            {/* Thread Details & Reply */}
                            <View style={styles.threadCard}>
                                {selectedTicket ? (
                                    <>
                                        <View style={styles.threadHeader}>
                                            <View>
                                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                                    <Text style={styles.threadTitle}>{selectedTicket.subject}</Text>
                                                    <StatusPill status={selectedTicket.status} />
                                                </View>
                                                <Text style={styles.threadSub}>
                                                    From: {selectedTicket.userName} ({selectedTicket.userEmail})
                                                    {selectedTicket.bookingId ? ` · Booking #${selectedTicket.bookingId}` : ''}
                                                </Text>
                                            </View>

                                            <View style={styles.statusChangeRow}>
                                                {(['open', 'in_progress', 'resolved', 'closed'] as const).map(s => (
                                                    <Pressable
                                                        key={s}
                                                        onPress={() => handleChangeTicketStatus(s)}
                                                        style={[
                                                            styles.statusBtn,
                                                            selectedTicket.status === s && styles.statusBtnActive
                                                        ]}
                                                    >
                                                        <Text
                                                            style={[
                                                                styles.statusBtnText,
                                                                selectedTicket.status === s && styles.statusBtnTextActive
                                                            ]}
                                                        >
                                                            {s.replace('_', ' ')}
                                                        </Text>
                                                    </Pressable>
                                                ))}
                                            </View>
                                        </View>

                                        {/* Messages Thread */}
                                        <ScrollView style={styles.messagesScroll} contentContainerStyle={styles.messagesContent}>
                                            {selectedTicket.messages.map((m, idx) => {
                                                const isAdminMsg = m.senderRole === 'admin';
                                                return (
                                                    <View
                                                        key={idx}
                                                        style={[
                                                            styles.messageBubble,
                                                            isAdminMsg ? styles.messageAdmin : styles.messageUser
                                                        ]}
                                                    >
                                                        <View style={styles.messageMeta}>
                                                            <Text style={[styles.senderName, isAdminMsg && styles.senderNameAdmin]}>
                                                                {m.senderName} {isAdminMsg ? '(Support Admin)' : ''}
                                                            </Text>
                                                        </View>
                                                        <Text style={[styles.messageText, isAdminMsg && styles.messageTextAdmin]}>
                                                            {m.text}
                                                        </Text>
                                                    </View>
                                                );
                                            })}
                                        </ScrollView>

                                        {/* Reply Box */}
                                        <View style={styles.replyBox}>
                                            <TextInput
                                                value={replyText}
                                                onChangeText={setReplyText}
                                                placeholder="Write reply to customer…"
                                                placeholderTextColor={colors.inkFaint}
                                                multiline
                                                style={styles.replyInput}
                                            />
                                            <Pressable
                                                onPress={handleSendReply}
                                                disabled={replying || !replyText.trim()}
                                                style={[styles.sendBtn, (!replyText.trim() || replying) && styles.sendBtnDisabled]}
                                            >
                                                {replying ? (
                                                    <ActivityIndicator size="small" color={colors.white} />
                                                ) : (
                                                    <Send size={16} color={colors.white} />
                                                )}
                                            </Pressable>
                                        </View>
                                    </>
                                ) : (
                                    <View style={styles.emptyThread}>
                                        <MessageSquare size={32} color={colors.inkFaint} />
                                        <Text style={styles.emptyTitle}>Select a ticket</Text>
                                        <Text style={styles.emptySub}>Click on a conversation in the queue to read and reply.</Text>
                                    </View>
                                )}
                            </View>
                        </View>
                    </View>
                ) : null}
            </View>

            {/* CMS Edit Modal */}
            {renderCmsEditModal()}

            <View style={{ height: spacing(6) }} />
        </Screen>
    );
}

function StatusPill({ status }: { status: string }) {
    const isSuccess = status === 'confirmed' || status === 'resolved';
    const isWarning = status === 'in_progress' || status === 'open';
    const isMuted = status === 'cancelled' || status === 'closed';

    return (
        <View
            style={[
                styles.statusPill,
                isSuccess && styles.statusPillSuccess,
                isWarning && styles.statusPillWarning,
                isMuted && styles.statusPillMuted
            ]}
        >
            <Text
                style={[
                    styles.statusPillText,
                    isSuccess && styles.statusPillTextSuccess,
                    isWarning && styles.statusPillTextWarning,
                    isMuted && styles.statusPillTextMuted
                ]}
            >
                {status.replace(/_/g, ' ')}
            </Text>
        </View>
    );
}

const styles = StyleSheet.create({
    body: { maxWidth: maxContentWidth, width: '100%', alignSelf: 'center', paddingHorizontal: spacing(3), paddingTop: spacing(3) },
    bodyCompact: { paddingHorizontal: spacing(2), paddingTop: spacing(2) },
    authBody: { paddingBottom: spacing(3) },
    header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing(3) },
    headerCompact: { alignItems: 'flex-start' },
    headerCopy: { flex: 1, minWidth: 0 },
    heading: { fontFamily: fontFamily.bold, fontSize: 24, color: colors.ink },
    headingCompact: { fontSize: 20 },
    refreshBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, borderWidth: 1, borderColor: colors.line, paddingHorizontal: spacing(1.5), paddingVertical: spacing(1), borderRadius: radii.md, backgroundColor: colors.card },
    refreshBtnCompact: { width: 36, height: 36, justifyContent: 'center', paddingHorizontal: 0, paddingVertical: 0 },
    refreshText: { fontFamily: fontFamily.medium, fontSize: 13, color: colors.inkSoft },
    noticeBar: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', backgroundColor: colors.skyTint, paddingHorizontal: spacing(2), paddingVertical: spacing(1.5), borderRadius: radii.md, marginBottom: spacing(2), borderWidth: 1, borderColor: colors.brandBright },
    noticeText: { fontFamily: fontFamily.medium, fontSize: 13, color: colors.brand },
    panel: { backgroundColor: colors.card, borderRadius: radii.lg, borderWidth: 1, borderColor: colors.line, padding: spacing(4), alignItems: 'center', gap: spacing(0.75) },
    icon: { width: 56, height: 56, borderRadius: radii.full, backgroundColor: colors.skyTint, alignItems: 'center', justifyContent: 'center', marginBottom: spacing(1) },
    title: { width: '100%', fontFamily: fontFamily.semibold, fontSize: 18, color: colors.ink, textAlign: 'center' },
    sub: { width: '100%', fontFamily: fontFamily.regular, fontSize: 14, lineHeight: 21, color: colors.inkFaint, textAlign: 'center' },
    cta: { marginTop: spacing(1.5), backgroundColor: colors.brand, paddingVertical: spacing(1.25), paddingHorizontal: spacing(3), borderRadius: radii.md },
    ctaText: { fontFamily: fontFamily.semibold, fontSize: 14, color: colors.white },
    menuNavScroll: { width: '100%', marginBottom: spacing(3.5) },
    menuNav: { flexGrow: 1, flexDirection: 'row', gap: spacing(0.5), backgroundColor: colors.card, padding: spacing(0.75), borderRadius: radii.lg, borderWidth: 1, borderColor: colors.line },
    menuItem: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: spacing(1.25), paddingHorizontal: spacing(2), borderRadius: radii.md },
    menuItemActive: { backgroundColor: colors.skyTint },
    menuItemText: { fontFamily: fontFamily.medium, fontSize: 14, color: colors.inkSoft },
    menuItemTextActive: { fontFamily: fontFamily.semibold, color: colors.brand },
    viewContent: { gap: spacing(3) },
    statsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing(2) },
    statCard: { flex: 1, minWidth: 160, backgroundColor: colors.card, borderRadius: radii.lg, borderWidth: 1, borderColor: colors.line, padding: spacing(3), gap: spacing(1.25) },
    statHeader: { flexDirection: 'row', alignItems: 'center', gap: 6 },
    statLabel: { fontFamily: fontFamily.medium, fontSize: 13, color: colors.inkFaint },
    statValue: { fontFamily: fontFamily.bold, fontSize: 24, color: colors.ink },
    twoCol: { flexDirection: 'row', gap: spacing(2) },
    oneCol: { flexDirection: 'column' },
    sectionCard: { flex: 1, backgroundColor: colors.card, borderRadius: radii.lg, borderWidth: 1, borderColor: colors.line, padding: spacing(3) },
    cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing(2.5), paddingBottom: spacing(1.5), borderBottomWidth: 1, borderBottomColor: colors.lineSoft },
    cardTitle: { fontFamily: fontFamily.semibold, fontSize: 16, color: colors.ink },
    linkText: { fontFamily: fontFamily.semibold, fontSize: 13, color: colors.brand },
    cardList: { gap: spacing(2) },
    overviewRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: spacing(0.5) },
    avatar: { width: 36, height: 36, borderRadius: radii.full, backgroundColor: colors.skyTint, alignItems: 'center', justifyContent: 'center' },
    avatarText: { fontFamily: fontFamily.bold, fontSize: 14, color: colors.brand },
    rowTitle: { fontFamily: fontFamily.medium, fontSize: 14, color: colors.ink },
    rowSub: { fontFamily: fontFamily.regular, fontSize: 12, color: colors.inkFaint },
    rowTime: { fontFamily: fontFamily.regular, fontSize: 12, color: colors.inkFaint },
    roleBadge: { backgroundColor: colors.greenTint, paddingHorizontal: 6, paddingVertical: 2, borderRadius: radii.sm },
    roleText: { fontFamily: fontFamily.semibold, fontSize: 10, color: colors.green },
    cmsHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: spacing(1.5) },
    cmsHeaderCompact: { flexDirection: 'column', alignItems: 'stretch' },
    cmsSubNavScroll: { flexShrink: 1, maxWidth: '100%' },
    cmsSubNavScrollCompact: { width: '100%' },
    cmsSubNav: { flexDirection: 'row', gap: spacing(1), backgroundColor: colors.card, padding: 4, borderRadius: radii.md, borderWidth: 1, borderColor: colors.line },
    cmsSubTab: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: spacing(0.75), paddingHorizontal: spacing(1.5), borderRadius: radii.sm },
    cmsSubTabActive: { backgroundColor: colors.skyTint },
    cmsSubTabText: { fontFamily: fontFamily.medium, fontSize: 13, color: colors.inkSoft },
    cmsSubTabTextActive: { fontFamily: fontFamily.semibold, color: colors.brand },
    cmsActions: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing(1) },
    cmsActionsCompact: { width: '100%' },
    importPanel: { backgroundColor: colors.card, borderWidth: 1, borderColor: colors.brandLine, borderRadius: radii.lg, padding: spacing(2.5), gap: spacing(1.5), ...shadows.sm },
    importPanelHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing(1.5), paddingBottom: spacing(1.5), borderBottomWidth: 1, borderBottomColor: colors.lineSoft },
    importTitle: { fontFamily: fontFamily.semibold, fontSize: 15, color: colors.ink },
    importSubtitle: { fontFamily: fontFamily.regular, fontSize: 12, color: colors.inkFaint },
    importStatus: { paddingHorizontal: spacing(1.25), paddingVertical: spacing(0.625), borderRadius: radii.full },
    importStatusValid: { backgroundColor: colors.greenTint },
    importStatusInvalid: { backgroundColor: colors.pinkTint },
    importStatusText: { fontFamily: fontFamily.semibold, fontSize: 11 },
    importStatusTextValid: { color: colors.green },
    importStatusTextInvalid: { color: colors.pink },
    importSection: { gap: spacing(1.5) },
    importPreviewRow: { flexDirection: 'row', gap: spacing(2), paddingVertical: spacing(0.5) },
    importSectionRow: { flexDirection: 'row', alignItems: 'center', gap: spacing(2), paddingTop: spacing(1.5), borderTopWidth: 1, borderTopColor: colors.lineSoft },
    importSectionTitle: { fontFamily: fontFamily.semibold, fontSize: 14, color: colors.ink },
    importSectionMeta: { fontFamily: fontFamily.regular, fontSize: 12, color: colors.inkFaint },
    importErrorText: { fontFamily: fontFamily.regular, fontSize: 12, lineHeight: 17, color: colors.pink },
    importWarningText: { fontFamily: fontFamily.regular, fontSize: 12, lineHeight: 17, color: colors.inkSoft },
    importPublishDisabled: { opacity: 0.45 },
    secondaryActionBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, borderWidth: 1, borderColor: colors.line, paddingVertical: spacing(1), paddingHorizontal: spacing(1.5), borderRadius: radii.md, backgroundColor: colors.card },
    secondaryActionText: { fontFamily: fontFamily.medium, fontSize: 13, color: colors.inkSoft },
    primaryActionBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: colors.brand, paddingVertical: spacing(1), paddingHorizontal: spacing(2), borderRadius: radii.md },
    primaryActionText: { fontFamily: fontFamily.semibold, fontSize: 13, color: colors.white },
    cmsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing(2) },
    cmsCard: { flexGrow: 1, flexBasis: 240, maxWidth: 360, backgroundColor: colors.card, borderRadius: radii.md, borderWidth: 1, borderColor: colors.line, overflow: 'hidden' },
    cmsImage: { width: '100%', height: 140, backgroundColor: colors.lineSoft },
    cmsImagePlaceholder: { width: '100%', height: 140, backgroundColor: colors.lineSoft },
    cmsCardBody: { padding: spacing(1.75), gap: spacing(0.75) },
    cmsCardTitleRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
    cmsCardTitle: { fontFamily: fontFamily.semibold, fontSize: 15, color: colors.ink, flex: 1 },
    cmsBadge: { backgroundColor: colors.skyTint, paddingHorizontal: 6, paddingVertical: 2, borderRadius: radii.sm, marginLeft: 6 },
    cmsBadgeText: { fontFamily: fontFamily.semibold, fontSize: 10, color: colors.brand },
    cmsCardSub: { fontFamily: fontFamily.regular, fontSize: 13, color: colors.inkFaint },
    priceRow: { flexDirection: 'row', alignItems: 'baseline', gap: 8, marginTop: 4 },
    cmsPrice: { fontFamily: fontFamily.bold, fontSize: 16, color: colors.ink },
    cmsGuestPrice: { fontFamily: fontFamily.regular, fontSize: 12, color: colors.inkFaint, textDecorationLine: 'line-through' },
    cmsCardActions: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: spacing(1), paddingTop: spacing(1), borderTopWidth: 1, borderTopColor: colors.lineSoft },
    editBtn: { paddingVertical: 4, paddingHorizontal: 8 },
    editBtnText: { fontFamily: fontFamily.semibold, fontSize: 13, color: colors.brand },
    deleteBtn: { padding: 4 },
    toolbar: { flexDirection: 'row', gap: spacing(1.5) },
    searchInput: { flex: 1, backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: radii.md, paddingHorizontal: spacing(2), paddingVertical: spacing(1.25), fontFamily: fontFamily.regular, fontSize: 14, color: colors.ink, ...({ outlineStyle: 'none' } as object) },
    tableCard: { backgroundColor: colors.card, borderRadius: radii.md, borderWidth: 1, borderColor: colors.line, overflow: 'hidden' },
    tableHeader: { flexDirection: 'row', backgroundColor: colors.lineSoft, paddingHorizontal: spacing(2), paddingVertical: spacing(1.5), borderBottomWidth: 1, borderBottomColor: colors.line },
    th: { fontFamily: fontFamily.semibold, fontSize: 12, color: colors.inkFaint, textTransform: 'uppercase' },
    tableRow: { flexDirection: 'row', paddingHorizontal: spacing(2), paddingVertical: spacing(1.5), borderBottomWidth: 1, borderBottomColor: colors.lineSoft, alignItems: 'center' },
    td: { justifyContent: 'center' },
    userName: { fontFamily: fontFamily.semibold, fontSize: 14, color: colors.ink },
    userEmail: { fontFamily: fontFamily.regular, fontSize: 12, color: colors.inkFaint },
    cellText: { fontFamily: fontFamily.regular, fontSize: 13, color: colors.inkSoft },
    rolePill: { alignSelf: 'flex-start', paddingHorizontal: 8, paddingVertical: 3, borderRadius: radii.sm },
    roleAdmin: { backgroundColor: colors.skyTint },
    roleMember: { backgroundColor: colors.lineSoft },
    rolePillText: { fontFamily: fontFamily.semibold, fontSize: 11 },
    roleAdminText: { color: colors.brand },
    roleMemberText: { color: colors.inkFaint },
    actionToggleBtn: { borderWidth: 1, borderColor: colors.line, paddingVertical: 4, paddingHorizontal: 10, borderRadius: radii.sm },
    actionToggleText: { fontFamily: fontFamily.medium, fontSize: 12, color: colors.inkSoft },
    bookingsList: { gap: spacing(2) },
    bookingCard: { flexDirection: 'row', gap: spacing(2), backgroundColor: colors.card, borderRadius: radii.md, borderWidth: 1, borderColor: colors.line, padding: spacing(2), alignItems: 'center' },
    bookingThumb: { width: 100, height: 80, borderRadius: radii.sm, backgroundColor: colors.lineSoft },
    bookingThumbPlaceholder: { width: 100, height: 80, borderRadius: radii.sm, backgroundColor: colors.lineSoft },
    bookingInfo: { flex: 1, gap: 3 },
    bookingMetaRow: { flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
    bookingIdText: { fontFamily: fontFamily.bold, fontSize: 13, color: colors.ink },
    bookingRefText: { fontFamily: fontFamily.regular, fontSize: 11, color: colors.inkFaint },
    bookingHotelName: { fontFamily: fontFamily.semibold, fontSize: 15, color: colors.ink },
    bookingCustomer: { fontFamily: fontFamily.regular, fontSize: 13, color: colors.inkSoft },
    bookingDatesRow: { flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 2 },
    bookingDates: { fontFamily: fontFamily.regular, fontSize: 12, color: colors.inkFaint },
    bookingActions: { alignItems: 'flex-end', gap: 8 },
    bookingPrice: { fontFamily: fontFamily.bold, fontSize: 18, color: colors.ink },
    cancelBtn: { borderWidth: 1, borderColor: colors.line, borderRadius: radii.sm, paddingVertical: 6, paddingHorizontal: 12, minWidth: 110, alignItems: 'center' },
    cancelBtnBusy: { opacity: 0.6 },
    cancelBtnText: { fontFamily: fontFamily.semibold, fontSize: 12, color: colors.pink },
    nonRefundableText: { fontFamily: fontFamily.regular, fontSize: 11, color: colors.inkFaint },
    ticketFilters: { flexDirection: 'row', gap: spacing(1), flexWrap: 'wrap' },
    filterPill: { paddingVertical: 6, paddingHorizontal: 12, borderRadius: radii.full, backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line },
    filterPillActive: { backgroundColor: colors.brand, borderColor: colors.brand },
    filterPillText: { fontFamily: fontFamily.semibold, fontSize: 11, color: colors.inkSoft },
    filterPillTextActive: { color: colors.white },
    ticketQueueCard: { flex: 1, backgroundColor: colors.card, borderRadius: radii.md, borderWidth: 1, borderColor: colors.line, padding: spacing(2) },
    queueHeader: { fontFamily: fontFamily.semibold, fontSize: 15, color: colors.ink, marginBottom: spacing(1.5) },
    queueList: { gap: spacing(1) },
    ticketRow: { padding: spacing(1.5), borderRadius: radii.sm, borderWidth: 1, borderColor: colors.lineSoft, gap: 4 },
    ticketRowActive: { borderColor: colors.brand, backgroundColor: colors.skyTint },
    ticketRowHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
    ticketId: { fontFamily: fontFamily.bold, fontSize: 13, color: colors.ink },
    ticketSubject: { fontFamily: fontFamily.medium, fontSize: 14, color: colors.ink },
    ticketUser: { fontFamily: fontFamily.regular, fontSize: 12, color: colors.inkFaint },
    threadCard: { flex: 1.5, backgroundColor: colors.card, borderRadius: radii.md, borderWidth: 1, borderColor: colors.line, minHeight: 450, display: 'flex' },
    threadHeader: { padding: spacing(2), borderBottomWidth: 1, borderBottomColor: colors.lineSoft, gap: spacing(1) },
    threadTitle: { fontFamily: fontFamily.bold, fontSize: 16, color: colors.ink },
    threadSub: { fontFamily: fontFamily.regular, fontSize: 13, color: colors.inkFaint },
    statusChangeRow: { flexDirection: 'row', gap: 6, flexWrap: 'wrap', marginTop: 4 },
    statusBtn: { borderWidth: 1, borderColor: colors.line, borderRadius: radii.sm, paddingHorizontal: 8, paddingVertical: 3 },
    statusBtnActive: { backgroundColor: colors.brand, borderColor: colors.brand },
    statusBtnText: { fontFamily: fontFamily.medium, fontSize: 11, color: colors.inkSoft, textTransform: 'capitalize' },
    statusBtnTextActive: { color: colors.white },
    messagesScroll: { flex: 1, padding: spacing(2) },
    messagesContent: { gap: spacing(1.5) },
    messageBubble: { maxWidth: '80%', padding: spacing(1.5), borderRadius: radii.md },
    messageUser: { alignSelf: 'flex-start', backgroundColor: colors.lineSoft },
    messageAdmin: { alignSelf: 'flex-end', backgroundColor: colors.skyTint },
    messageMeta: { marginBottom: 2 },
    senderName: { fontFamily: fontFamily.semibold, fontSize: 11, color: colors.inkFaint },
    senderNameAdmin: { color: colors.brand },
    messageText: { fontFamily: fontFamily.regular, fontSize: 13, color: colors.ink, lineHeight: 18 },
    messageTextAdmin: { color: colors.ink },
    replyBox: { flexDirection: 'row', padding: spacing(1.5), borderTopWidth: 1, borderTopColor: colors.lineSoft, gap: spacing(1), alignItems: 'flex-end' },
    replyInput: { flex: 1, backgroundColor: colors.cream, borderWidth: 1, borderColor: colors.line, borderRadius: radii.md, padding: spacing(1.25), maxHeight: 80, fontFamily: fontFamily.regular, fontSize: 13, color: colors.ink, ...({ outlineStyle: 'none' } as object) },
    sendBtn: { backgroundColor: colors.brand, padding: spacing(1.25), borderRadius: radii.md, alignItems: 'center', justifyContent: 'center' },
    sendBtnDisabled: { opacity: 0.5 },
    emptyThread: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: spacing(4), gap: 6 },
    emptyCard: { backgroundColor: colors.card, padding: spacing(4), borderRadius: radii.md, borderWidth: 1, borderColor: colors.line, alignItems: 'center', gap: 6 },
    emptyTitle: { fontFamily: fontFamily.semibold, fontSize: 15, color: colors.ink },
    emptySub: { fontFamily: fontFamily.regular, fontSize: 13, color: colors.inkFaint, textAlign: 'center' },
    statusPill: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: radii.full, backgroundColor: colors.lineSoft },
    statusPillSuccess: { backgroundColor: colors.greenTint },
    statusPillWarning: { backgroundColor: colors.skyTint },
    statusPillMuted: { backgroundColor: colors.lineSoft },
    statusPillText: { fontFamily: fontFamily.semibold, fontSize: 10, textTransform: 'capitalize' },
    statusPillTextSuccess: { color: colors.green },
    statusPillTextWarning: { color: colors.brand },
    statusPillTextMuted: { color: colors.inkFaint },
    cardHidBadge: { backgroundColor: colors.skyTint, paddingHorizontal: 5, paddingVertical: 1, borderRadius: radii.sm },
    cardHidBadgeText: { fontFamily: fontFamily.semibold, fontSize: 9, color: colors.brand },
    cardRatingBadge: { flexDirection: 'row', alignItems: 'center', gap: 2, backgroundColor: colors.greenTint, paddingHorizontal: 5, paddingVertical: 1, borderRadius: radii.sm },
    cardRatingText: { fontFamily: fontFamily.bold, fontSize: 9, color: colors.green },
    selectedHotelCard: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing(1.5),
        backgroundColor: colors.cream,
        borderWidth: 1,
        borderColor: colors.line,
        borderRadius: radii.md,
        padding: spacing(1.5),
        marginTop: 4
    },
    selectedHotelThumb: { width: 64, height: 64, borderRadius: radii.sm, backgroundColor: colors.lineSoft },
    selectedHotelThumbPlaceholder: { width: 64, height: 64, borderRadius: radii.sm, backgroundColor: colors.lineSoft, alignItems: 'center', justifyContent: 'center' },
    selectedHotelInfo: { flex: 1, gap: 3 },
    selectedHotelName: { fontFamily: fontFamily.bold, fontSize: 14, color: colors.ink },
    selectedHotelMeta: { flexDirection: 'row', alignItems: 'center', gap: 4 },
    selectedHotelLocation: { fontFamily: fontFamily.regular, fontSize: 12, color: colors.inkSoft },
    selectedHotelBadges: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 2 },
    hidTag: { backgroundColor: colors.lineSoft, paddingHorizontal: 6, paddingVertical: 2, borderRadius: radii.sm },
    hidTagText: { fontFamily: fontFamily.semibold, fontSize: 10, color: colors.inkSoft },
    ratingTag: { flexDirection: 'row', alignItems: 'center', gap: 3, backgroundColor: colors.skyTint, paddingHorizontal: 6, paddingVertical: 2, borderRadius: radii.sm },
    ratingTagText: { fontFamily: fontFamily.bold, fontSize: 10, color: colors.brand },
    changeHotelBtn: { paddingVertical: 6, paddingHorizontal: 10, borderRadius: radii.sm, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.card },
    changeHotelBtnText: { fontFamily: fontFamily.medium, fontSize: 12, color: colors.inkSoft },
    hotelSearchSection: {
        gap: 6,
        position: 'relative',
        zIndex: 50
    },
    hotelSearchAnchor: {
        position: 'relative',
        zIndex: 100,
        width: '100%'
    },
    hotelSearchInputContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: colors.line,
        borderRadius: radii.md,
        backgroundColor: colors.cream
    },
    hotelSearchInputContainerFocused: {
        borderColor: colors.brand
    },
    hotelSearchInput: {
        flex: 1,
        paddingHorizontal: spacing(1),
        paddingVertical: spacing(1.2),
        fontFamily: fontFamily.regular,
        fontSize: 14,
        color: colors.ink,
        backgroundColor: 'transparent',
        ...({ outlineStyle: 'none' } as object)
    },
    hotelSearchResultsDropdown: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        marginTop: 4,
        zIndex: 1000,
        backgroundColor: colors.card,
        borderRadius: radii.md,
        borderWidth: 1,
        borderColor: colors.line,
        ...shadows.card,
        elevation: 8,
        maxHeight: 220,
        overflow: 'hidden'
    },
    hotelSearchResultItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        padding: spacing(1),
        borderBottomWidth: 1,
        borderBottomColor: colors.lineSoft
    },
    hotelSearchResultItemHovered: {
        backgroundColor: colors.skyTint
    },
    searchResultThumb: { width: 44, height: 44, borderRadius: radii.sm, backgroundColor: colors.lineSoft },
    searchResultThumbPlaceholder: { width: 44, height: 44, borderRadius: radii.sm, backgroundColor: colors.lineSoft, alignItems: 'center', justifyContent: 'center' },
    searchResultName: { fontFamily: fontFamily.semibold, fontSize: 13, color: colors.ink },
    searchResultSub: { fontFamily: fontFamily.regular, fontSize: 11, color: colors.inkFaint },
    searchResultRating: { flexDirection: 'row', alignItems: 'center', gap: 3, backgroundColor: colors.skyTint, paddingHorizontal: 5, paddingVertical: 2, borderRadius: radii.sm },
    searchResultRatingText: { fontFamily: fontFamily.bold, fontSize: 10, color: colors.brand },
    noResultsDropdown: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        marginTop: 4,
        backgroundColor: colors.card,
        borderWidth: 1,
        borderColor: colors.line,
        borderRadius: radii.md,
        padding: spacing(1.5),
        zIndex: 20
    },
    noResultsBox: { padding: spacing(1.5), backgroundColor: colors.lineSoft, borderRadius: radii.sm, alignItems: 'center' },
    noResultsText: { fontFamily: fontFamily.regular, fontSize: 12, color: colors.inkFaint },
    overridesToggle: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 10, paddingVertical: 4 },
    overridesToggleText: { fontFamily: fontFamily.medium, fontSize: 12, color: colors.inkSoft },
    overridesBox: { gap: spacing(1), padding: spacing(1.5), backgroundColor: colors.cream, borderRadius: radii.md, borderWidth: 1, borderColor: colors.lineSoft, marginTop: 4 },
    modalOverlay: { flex: 1, backgroundColor: 'rgba(15, 23, 42, 0.6)', justifyContent: 'center', alignItems: 'center', padding: spacing(2) },
    modalCard: { width: '100%', maxWidth: 520, backgroundColor: colors.card, borderRadius: radii.lg, padding: spacing(3), maxHeight: '90%' },
    modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing(2) },
    modalTitle: { fontFamily: fontFamily.bold, fontSize: 18, color: colors.ink },
    modalForm: { gap: spacing(1.5) },
    fieldLabel: { fontFamily: fontFamily.semibold, fontSize: 13, color: colors.ink, marginBottom: 4, marginTop: 8 },
    formInput: { borderWidth: 1, borderColor: colors.line, borderRadius: radii.md, paddingHorizontal: spacing(1.5), paddingVertical: spacing(1), fontFamily: fontFamily.regular, fontSize: 14, color: colors.ink, backgroundColor: colors.cream, ...({ outlineStyle: 'none' } as object) },
    modalFooter: { flexDirection: 'row', justifyContent: 'flex-end', gap: spacing(1.5), marginTop: spacing(2.5), paddingTop: spacing(1.5), borderTopWidth: 1, borderTopColor: colors.lineSoft },
    paginationCard: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', backgroundColor: colors.card, borderRadius: radii.md, borderWidth: 1, borderColor: colors.line, padding: spacing(1.5), marginTop: spacing(1) },
    paginationInfo: { fontFamily: fontFamily.regular, fontSize: 13, color: colors.inkSoft },
    paginationActions: { flexDirection: 'row', gap: spacing(1), alignItems: 'center' },
    pageBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, borderWidth: 1, borderColor: colors.line, borderRadius: radii.sm, paddingVertical: 6, paddingHorizontal: 12, backgroundColor: colors.cream },
    pageBtnDisabled: { opacity: 0.4 },
    pageBtnText: { fontFamily: fontFamily.medium, fontSize: 12, color: colors.ink },
    pageBtnTextDisabled: { color: colors.inkFaint }
});

export default AdminScreen;
